import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Spinner,
  Text,
  VStack,
  HStack,
  Badge,
  useToast,
  IconButton,
  Tooltip,
  Switch,
} from "@chakra-ui/react";
import { GoogleMap, useJsApiLoader, Circle, Polyline, TrafficLayer } from "@react-google-maps/api";
import MovingVehicleMarker from "./MovingVehicleMarker";
import { haversineDistanceMeters } from "../utils/geo";
import { httpsCallable } from "firebase/functions";
import { MdMyLocation } from "react-icons/md";
import { functions as firebaseFunctions } from "../firebase";

interface GPSLocation {
  imei: string;
  deviceName: string;
  lat: number;
  lng: number;
  gpsTime: string;
  speed: number;
  accStatus: string;
  status: string;
  direction: string;
  batteryLevel?: string;
}

interface DeliveryLocationData {
  delivery: {
    id: string;
    route: string;
    destination: string;
    wayBillNumber?: string;
    driverName: string;
    plateNumber: string;
  };
  vehicle: {
    id: string;
    plateNo: string;
    gpsDeviceImei: string;
  };
  location: GPSLocation;
}

interface LiveTrackingData {
  lat: number;
  lng: number;
  speed: number;
  gpsTime: string;
  accStatus: string;
  status: string;
  address: string;
}

interface VehicleMapModalProps {
  isOpen: boolean;
  onClose: () => void;
  deliveryId: string;
  deliveryData?: {
    wayBillNumber?: string;
    route?: string;
    driverName?: string;
    plateNumber?: string;
    destination?: string;
  };
  originLat?: number;
  originLng?: number;
  destinationLat?: number;
  destinationLng?: number;
  geofenceRadius?: number;
}

const VehicleMapModal: React.FC<VehicleMapModalProps> = ({
  isOpen,
  onClose,
  deliveryId,
  deliveryData,
  originLat,
  originLng,
  destinationLat,
  destinationLng,
  geofenceRadius,
}) => {
  const [loading, setLoading] = useState(true);
  const [locationData, setLocationData] = useState<DeliveryLocationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [liveTrackingData, setLiveTrackingData] = useState<LiveTrackingData | null>(null);
  const [liveTrace, setLiveTrace] = useState<{lat:number,lng:number,gpsTime:string,speed:number}[]>([]);
  const [prevLivePoint, setPrevLivePoint] = useState<{lat:number,lng:number}|null>(null);
  const [liveTail, setLiveTail] = useState<{lat:number,lng:number}[]>([]);
  const [address, _setAddress] = useState<string>('');
  const [addressLoading, _setAddressLoading] = useState(false);
  const [showOrigin, setShowOrigin] = useState(false);
  const [showDestination, setShowDestination] = useState(false);
  const [autoCenterLive, setAutoCenterLive] = useState<boolean>(true);
  const [showTraffic, setShowTraffic] = useState<boolean>(false);
  // Live auto-refresh interval (seconds). Change this constant to adjust polling.
  const LIVE_AUTO_REFRESH_SECS = 10; // default 10s (10 seconds)
  const toast = useToast();
  
  // Refs
  const isMountedRef = React.useRef(true);
  const liveIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const geocodeAbortRef = React.useRef<boolean>(false);
  const originCircleRef = React.useRef<google.maps.Circle | null>(null);
  const destinationCircleRef = React.useRef<google.maps.Circle | null>(null);
  const markerRetryRef = React.useRef<NodeJS.Timeout | null>(null);
  const markerRetryCountRef = React.useRef<number>(0);
  const scheduledFetchRef = React.useRef<boolean>(false);
  

  const mapContainerStyle = {
    width: "100%",
    height: "400px",
  };

  // Load Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "",
    id: "google-map-script",
  });

  // Open in Google Maps
  const openInGoogleMaps = () => {
    try {
      const lat = liveTrackingData?.lat || locationData?.location.lat;
      const lng = liveTrackingData?.lng || locationData?.location.lng;

      if (typeof lat !== 'number' || typeof lng !== 'number' || 
          isNaN(lat) || isNaN(lng) || 
          lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        toast({
          title: "Invalid Location",
          description: "Location coordinates are invalid",
          status: "error",
          duration: 3000,
        });
        return;
      }

      // Check if mobile device
      const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      if (isMobileDevice) {
        // Open in Google Maps app
        window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
      } else {
        // Open in new window with Street View
        window.open(`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}&heading=0&pitch=0&fov=80`, '_blank');
      }
    } catch (error) {
      console.error('Error opening Google Maps:', error);
      try { setError(String(error)); } catch {}
      toast({
        title: "Error",
        description: "Failed to open Google Maps",
        status: "error",
        duration: 3000,
      });
    }
  };

  const centerOnVehicle = () => {
    if (map) {
      const lat = liveTrackingData?.lat || locationData?.location.lat;
      const lng = liveTrackingData?.lng || locationData?.location.lng;
      
      if (lat && lng) {
        const vehiclePosition = { lat, lng };
        map.setCenter(vehiclePosition);
        map.setZoom(15);
      }
    }
  };

  
  

  // Manually control destination circle visibility
  React.useEffect(() => {
    if (destinationCircleRef.current && map) {
      if (showDestination) {
        destinationCircleRef.current.setMap(map);
        destinationCircleRef.current.setVisible(true);
      } else {
        destinationCircleRef.current.setVisible(false);
        destinationCircleRef.current.setMap(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDestination]);

  // GPS → Pixel conversion for live tracking
  const updateLiveMarkerPosition = React.useCallback(() => {
    if (!map || !liveTrackingData) return;

    const projection = map.getProjection();
    if (!projection) return;

    const vehicleLatLng = new google.maps.LatLng(
      liveTrackingData.lat,
      liveTrackingData.lng
    );

    const bounds = map.getBounds();
    if (!bounds) return;

    const worldCoordinate = projection.fromLatLngToPoint(vehicleLatLng);
    if (!worldCoordinate) return;

    const mapBoundsNE = bounds.getNorthEast();
    const mapBoundsSW = bounds.getSouthWest();
    const topRight = projection.fromLatLngToPoint(mapBoundsNE);
    const bottomLeft = projection.fromLatLngToPoint(mapBoundsSW);

    if (!topRight || !bottomLeft) return;

    const mapDiv = map.getDiv();
    const width = mapDiv.offsetWidth;
    const height = mapDiv.offsetHeight;

    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      if (markerRetryCountRef.current < 5) {
        markerRetryCountRef.current += 1;
        if (markerRetryRef.current) clearTimeout(markerRetryRef.current);
        markerRetryRef.current = setTimeout(() => {
          markerRetryRef.current = null;
          updateLiveMarkerPosition();
        }, 300);
      }
      return;
    }

    const x = ((worldCoordinate.x - bottomLeft.x) / (topRight.x - bottomLeft.x)) * width;
    const y = ((worldCoordinate.y - topRight.y) / (bottomLeft.y - topRight.y)) * height;

      if (Number.isFinite(x) && Number.isFinite(y)) {
      markerRetryCountRef.current = 0;
      if (markerRetryRef.current) {
        clearTimeout(markerRetryRef.current);
        markerRetryRef.current = null;
      }
      // Position updated via OverlayView marker component; no-op here
    } else {
      console.warn('Invalid live marker pixel coordinates, scheduling retry', { x, y, width, height });
      if (markerRetryCountRef.current < 5) {
        markerRetryCountRef.current += 1;
        if (markerRetryRef.current) clearTimeout(markerRetryRef.current);
        markerRetryRef.current = setTimeout(() => {
          markerRetryRef.current = null;
          updateLiveMarkerPosition();
        }, 300);
      }
    }
  }, [map, liveTrackingData]);

  // Helper: check if a LatLng is within a central inset percentage of the map
  const isLatLngWithinInset = React.useCallback((mapInstance: google.maps.Map, latlng: { lat: number; lng: number }, insetPct = 0.5) => {
    try {
      const bounds = mapInstance.getBounds();
      if (!bounds) return false;
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const lngSpan = ne.lng() - sw.lng();
      const latSpan = ne.lat() - sw.lat();
      const left = sw.lng() + lngSpan * insetPct;
      const right = ne.lng() - lngSpan * insetPct;
      const bottom = sw.lat() + latSpan * insetPct;
      const top = ne.lat() - latSpan * insetPct;
      return latlng.lng >= left && latlng.lng <= right && latlng.lat >= bottom && latlng.lat <= top;
    } catch (e) {
      return false;
    }
  }, []);

  // Auto-center when live data updates — only for scheduled interval fetches
  React.useEffect(() => {
    if (map && liveTrackingData && scheduledFetchRef.current) {
      try {
        const vehiclePos = { lat: liveTrackingData.lat, lng: liveTrackingData.lng };
        if (autoCenterLive) {
          try {
            // Use inset/hysteresis: only recenter if vehicle outside the central inset region
            const inside = isLatLngWithinInset(map, vehiclePos, 0.5);
            if (!inside) {
              map.panTo(vehiclePos);
            }
          } catch (e) {
            map.panTo(vehiclePos);
          }
        }
        if (typeof (map as any).getBounds === 'function') {
          const idleListener = map.addListener('idle', () => {
            updateLiveMarkerPosition();
            google.maps.event.removeListener(idleListener);
          });
        } else {
          updateLiveMarkerPosition();
        }
      } catch (err) {
        console.warn('Auto-center failed (mobile)', err);
      } finally {
        scheduledFetchRef.current = false;
      }
    }
  }, [liveTrackingData, map, updateLiveMarkerPosition, autoCenterLive]);

  React.useEffect(() => {
    return () => {
      if (markerRetryRef.current) {
        clearTimeout(markerRetryRef.current);
        markerRetryRef.current = null;
      }
      markerRetryCountRef.current = 0;
    };
  }, []);

  // Update live marker position on map changes
  React.useEffect(() => {
    if (!map || !liveTrackingData) {
      // Position managed by OverlayView; nothing to clear here
      return;
    }

    updateLiveMarkerPosition();

    const idleListener = map.addListener("idle", updateLiveMarkerPosition);

    return () => {
      google.maps.event.removeListener(idleListener);
    };
  }, [map, liveTrackingData, updateLiveMarkerPosition]);

  // Reverse geocoding disabled for live updates on mobile to avoid API cost.
  // Only perform reverse-geocoding on demand (e.g., current mode or manual refresh).

  // Fetch live GPS data
  const fetchLiveGPS = React.useCallback(async () => {
    if (!isMountedRef.current || geocodeAbortRef.current) {
      return;
    }

    setLoading(true);

    try {
      if (!firebaseFunctions) {
        throw new Error('Firebase Functions is not initialized. Check VITE_FIREBASE_* build environment variables.');
      }
      const getDeliveryLocation = httpsCallable(firebaseFunctions, "getDeliveryLocation");
      const result = await getDeliveryLocation({ deliveryId });
      
      if (!isMountedRef.current || geocodeAbortRef.current) {
        return;
      }
      
      const response = result.data as { success: boolean; data: DeliveryLocationData };

      if (response.success && response.data?.location) {
        const loc = response.data.location;

        // store full delivery+vehicle+location payload for UI
        setLocationData(response.data);
        
        if (typeof loc.lat !== 'number' || typeof loc.lng !== 'number') {
          console.error('Invalid location data received:', loc);
          return;
        }
        
        const newLiveData = {
          lat: loc.lat,
          lng: loc.lng,
          speed: loc.speed || 0,
          gpsTime: loc.gpsTime,
          accStatus: loc.accStatus,
          status: loc.status,
          address: 'Resolving...',
        };
        
        if (isMountedRef.current && !geocodeAbortRef.current) {
          setLiveTrackingData(newLiveData);

          // Append to preserved live trace with threshold and update prevLivePoint to
          // the last preserved sample (backward-looking point) to compute headings.
          setLiveTrace(prev => {
            const newPoint = { lat: loc.lat, lng: loc.lng, gpsTime: loc.gpsTime, speed: loc.speed || 0 };
            if (prev.length > 0) {
              const last = prev[prev.length - 1];
              const dist = haversineDistanceMeters({ lat: last.lat, lng: last.lng }, { lat: newPoint.lat, lng: newPoint.lng });
              if (dist < 8) {
                return prev; // skip small jitter
              }
            }
            const next = [...prev, newPoint];
            if (next.length > 1000) return next.slice(next.length - 1000);
            // update prevLivePoint to the previously preserved sample (penultimate)
            if (next.length >= 2) {
              const pen = next[next.length - 2];
              setPrevLivePoint({ lat: pen.lat, lng: pen.lng });
            } else if (next.length === 1) {
              setPrevLivePoint({ lat: newPoint.lat, lng: newPoint.lng });
            }
            return next;
          });
          setLiveTail(prev => {
            const next = [...prev, { lat: loc.lat, lng: loc.lng }];
            if (next.length > 8) return next.slice(next.length - 8);
            return next;
          });
        }

        // Reverse geocode asynchronously
        if (isLoaded && !geocodeAbortRef.current) {
          try {
            const geocoder = new google.maps.Geocoder();
            const latlng = { lat: loc.lat, lng: loc.lng };
            geocoder.geocode({ location: latlng }, (results, status) => {
              if (!isMountedRef.current || geocodeAbortRef.current) {
                return;
              }

              if (status === 'OK' && results && results[0]) {
                setLiveTrackingData(prev => {
                  if (!prev || !isMountedRef.current) return prev;
                  return { ...prev, address: results[0].formatted_address };
                });
                if (isMountedRef.current) setLoading(false);
              }
            });
          } catch (err) {
            console.error('Reverse geocode failed', err);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching live GPS:", error);
      if (isMountedRef.current && !geocodeAbortRef.current) {
        toast({
          title: "GPS Update Failed",
          description: "Failed to fetch latest GPS data",
          status: "warning",
          duration: 2000,
        });
      }
    }
  }, [deliveryId, isLoaded, toast]);

  const stopLivePolling = React.useCallback(() => {
    if (liveIntervalRef.current) {
      clearInterval(liveIntervalRef.current);
      liveIntervalRef.current = null;
    }
    geocodeAbortRef.current = true;
    scheduledFetchRef.current = false;
  }, []);

  const startLivePolling = React.useCallback((intervalMs = 60000) => {
    if (liveIntervalRef.current) {
      clearInterval(liveIntervalRef.current);
      liveIntervalRef.current = null;
    }
    geocodeAbortRef.current = false;
    // initial fetch
    fetchLiveGPS();

    const id = setInterval(() => {
      if (isMountedRef.current && !geocodeAbortRef.current) {
        scheduledFetchRef.current = true;
        fetchLiveGPS();
      }
    }, intervalMs);

    liveIntervalRef.current = id as unknown as NodeJS.Timeout;
  }, [fetchLiveGPS]);

  // Start/stop live tracking polling when modal opens
  React.useEffect(() => {
    if (isOpen) {
      const pollMs = Math.max(1000, LIVE_AUTO_REFRESH_SECS * 1000);
      startLivePolling(pollMs);
      return () => { stopLivePolling(); };
    }
    stopLivePolling();
  }, [isOpen, startLivePolling, stopLivePolling]);

  


  const formatGpsTime = (gpsTime: string) => {
    try {
      const date = new Date(gpsTime);
      return date.toLocaleString();
    } catch {
      return gpsTime;
    }
  };

  const getStatusColor = (status: string) => {
    return status === "1" ? "green" : "red";
  };

  const getAccStatusText = (accStatus: string) => {
    return accStatus === "1" ? "ON" : "OFF";
  };

  const getDeviceStateColor = (state?: string) => {
    switch (state) {
      case "RUNNING": return "green";
      case "IDLE": return "yellow";
      case "STOPPED": return "orange";
      case "PARKED": return "blue";
      case "OFFLINE": return "red";
      default: return "gray";
    }
  };

  if (loadError) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="full" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Error</ModalHeader>
          <ModalCloseButton />
          <ModalBody maxH={{ base: '60vh', md: '50vh' }} overflowY="auto">
            <Text color="red.500">Failed to load Google Maps</Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  if (!isLoaded || loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="full" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Vehicle Location</ModalHeader>
          <ModalCloseButton />
          <ModalBody maxH={{ base: '70vh', md: '60vh' }} overflowY="auto">
            <Box display="flex" justifyContent="center" alignItems="center" height={{ base: '350px', md: '400px' }}>
              <Spinner size="xl" color="blue.500" />
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  if (error || !locationData) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="full" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Vehicle Location</ModalHeader>
          <ModalCloseButton />
          <ModalBody maxH={{ base: '70vh', md: '60vh' }} overflowY="auto">
            <Box display="flex" justifyContent="center" alignItems="center" height={{ base: '350px', md: '400px' }}>
              <Text color="red.500">{error || "Failed to load vehicle location"}</Text>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  const { delivery, vehicle, location } = locationData;
  
  // Use pre-fetched delivery data if available, otherwise use fetched data
  const deliveryInfo = deliveryData ? {
    ...delivery,
    wayBillNumber: deliveryData.wayBillNumber || delivery.wayBillNumber,
    route: deliveryData.route || delivery.route,
    driverName: deliveryData.driverName || delivery.driverName,
    plateNumber: deliveryData.plateNumber || delivery.plateNumber,
    destination: deliveryData.destination || delivery.destination,
  } : delivery;

  const displayData = liveTrackingData ? {
    speed: liveTrackingData.speed,
    status: liveTrackingData.status,
    accStatus: liveTrackingData.accStatus,
    gpsTime: liveTrackingData.gpsTime,
    batteryLevel: location.batteryLevel,
  } : {
    speed: location.speed,
    status: location.status,
    accStatus: location.accStatus,
    gpsTime: location.gpsTime,
    batteryLevel: location.batteryLevel,
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">
          🚗 Live Tracking - {vehicle.plateNo}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} maxH={{ base: '75vh', md: '70vh' }} overflowY="auto" sx={{
          '&::-webkit-scrollbar': { width: '10px', height: '10px' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.25)', borderRadius: '8px' },
        }}>
          <VStack spacing={4} align="stretch">
            {/* Delivery Info */}
            <Box p={3} borderWidth={1} borderRadius="md" bg="gray.50">
              <VStack align="stretch" spacing={2}>
                <HStack justify="space-between">
                  <Text fontSize="xs" color="gray.600">Route:</Text>
                  <Text fontWeight="bold" fontSize="sm">{deliveryInfo.route}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="xs" color="gray.600">Waybill:</Text>
                  <Text fontWeight="bold" fontSize="sm">{deliveryInfo.wayBillNumber || 'N/A'}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="xs" color="gray.600">Driver:</Text>
                  <Text fontWeight="bold" fontSize="sm">{deliveryInfo.driverName}</Text>
                </HStack>
              </VStack>
            </Box>

            {/* Location Checkboxes */}
            {originLat && originLng && (
              <HStack spacing={2} align="center">
                <input
                  type="checkbox"
                  id="show-origin"
                  checked={showOrigin}
                  onChange={(e) => setShowOrigin(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <label htmlFor="show-origin" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Text fontSize="xs" color="green.600" fontWeight="medium">
                    📍 Plant Location (500m radius)
                  </Text>
                </label>
              </HStack>
            )}

            {/* Destination Circle Checkbox */}
            {destinationLat && destinationLng && (
              <HStack spacing={2} align="center">
                <input
                  type="checkbox"
                  id="show-destination"
                  checked={showDestination}
                  onChange={(e) => setShowDestination(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <label htmlFor="show-destination" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Text fontSize="xs" color="red.600" fontWeight="medium">
                    🏁 Delivery Location (500m radius)
                  </Text>
                </label>
              </HStack>
            )}


            {/* GPS Status */}
            <Box p={3} borderWidth={1} borderRadius="md">
              <HStack spacing={4} wrap="wrap" justify="space-between">
                <VStack align="start" spacing={1}>
                  <Text fontSize="xs" color="gray.600">Status</Text>
                  <Badge colorScheme={getStatusColor(displayData.status)} fontSize="xs">
                    {displayData.status === "1" ? "Online" : "Offline"}
                  </Badge>
                  {location && (location as any).deviceState && (
                    <Badge colorScheme={getDeviceStateColor((location as any).deviceState)} fontSize="xs">
                      {(location as any).deviceState}
                    </Badge>
                  )}
                </VStack>
                <VStack align="start" spacing={1}>
                  <Text fontSize="xs" color="gray.600">Engine</Text>
                  <Text fontWeight="medium" fontSize="sm">
                    {getAccStatusText(displayData.accStatus)}
                  </Text>
                </VStack>
                <VStack align="start" spacing={1}>
                  <Text fontSize="xs" color="gray.600">Speed</Text>
                  <Text fontWeight="medium" fontSize="sm">{displayData.speed} km/h</Text>
                </VStack>
              </HStack>
              <HStack spacing={4} wrap="wrap" justify="space-between" mt={3}>
                <VStack align="start" spacing={1}>
                  <Text fontSize="xs" color="gray.600">Last Update</Text>
                  <Text fontWeight="medium" fontSize="xs">
                    {formatGpsTime(displayData.gpsTime)}
                  </Text>
                </VStack>
                {displayData.batteryLevel && (
                  <VStack align="start" spacing={1}>
                    <Text fontSize="xs" color="gray.600">Battery</Text>
                    <Text fontWeight="medium" fontSize="sm">
                      🔋 {displayData.batteryLevel}%
                    </Text>
                  </VStack>
                )}
              </HStack>
            </Box>

              {/* Address (moved to be last in the info sequence) */}
              <Box p={3} borderWidth={1} borderRadius="md" bg="gray.50">
                <Text fontSize="xs" color="gray.600">Current Address</Text>
                {addressLoading ? (
                  <Text fontSize="sm" color="gray.500">Resolving address...</Text>
                ) : (
                  <Text fontWeight="medium" fontSize="sm">{address || 'Address not available'}</Text>
                )}
              </Box>

            {/* Action Buttons */}
            <HStack spacing={2}>
              <Tooltip label="Center on Vehicle" placement="top">
                <IconButton
                  aria-label="Center on vehicle"
                  icon={<MdMyLocation />}
                  onClick={centerOnVehicle}
                  size="sm"
                  colorScheme="orange"
                  fontSize="20px"
                  flex="0"
                />
              </Tooltip>
              <HStack spacing={2} align="center">
                <Text fontSize="xs" color="gray.600">Auto-center</Text>
                <Switch size="sm" isChecked={autoCenterLive} onChange={(e) => setAutoCenterLive(e.target.checked)} />
              </HStack>
              <HStack spacing={2} align="center">
                <Text fontSize="xs" color="gray.600">Traffic (LIVE)</Text>
                <Switch size="sm" isChecked={showTraffic} onChange={(e) => setShowTraffic(e.target.checked)} />
              </HStack>
              <Box>
                <Text fontSize="xs" color="gray.600">{`Auto-Refresh: ON (every ${LIVE_AUTO_REFRESH_SECS}s)`}</Text>
              </Box>
              <button
                onClick={openInGoogleMaps}
                style={{
                  flex: '1',
                  padding: '10px',
                  backgroundColor: '#48BB78',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                Open Map
              </button>
            </HStack>

            {/* Map */}
            <Box borderWidth={1} borderRadius="md" overflow="hidden" position="relative">
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                zoom={15}
                onLoad={(mapInstance) => {
                  setMap(mapInstance);
                  // Only center once when map loads
                  const initialCenter = liveTrackingData
                    ? { lat: liveTrackingData.lat, lng: liveTrackingData.lng }
                    : { lat: location.lat, lng: location.lng };
                  mapInstance.setCenter(initialCenter);
                }}
                options={{
                  streetViewControl: false,
                  mapTypeControl: true,
                  fullscreenControl: false,
                }}
              >
                {/* Origin Circle - Plant Location */}
                {originLat && originLng && (
                  <Circle
                    center={{ lat: originLat, lng: originLng }}
                    radius={geofenceRadius || 500}
                    options={{
                      fillColor: "#48BB78",
                      fillOpacity: 0.2,
                      strokeColor: "#38A169",
                      strokeOpacity: 0.8,
                      strokeWeight: 2,
                      visible: false,
                    }}
                    onLoad={(circle) => {
                      originCircleRef.current = circle;
                      if (showOrigin && map) {
                        circle.setMap(map);
                        circle.setVisible(true);
                      } else {
                        circle.setMap(null);
                      }
                    }}
                    onUnmount={() => {
                      if (originCircleRef.current) {
                        originCircleRef.current.setMap(null);
                        originCircleRef.current = null;
                      }
                    }}
                  />
                )}

                    {/* Live Trace Polyline - preserve while modal open */}
                    {liveTrace.length > 0 && (
                      <Polyline
                        path={liveTrace.map(p => ({ lat: p.lat, lng: p.lng }))}
                        options={{ strokeColor: '#00c853', strokeOpacity: 0.9, strokeWeight: 4, zIndex: 999 }}
                      />
                    )}

                    {/* Live Tail (recent points) */}
                    {liveTail && liveTail.length > 1 && (
                      <Polyline
                        path={liveTail.map(p => ({ lat: p.lat, lng: p.lng }))}
                        options={{ strokeColor: '#00c853', strokeOpacity: 0.9, strokeWeight: 6, zIndex: 1000 }}
                      />
                    )}

                    {/* Moving vehicle overlay marker */}
                    {liveTrackingData && (
                      <MovingVehicleMarker
                        lat={liveTrackingData.lat}
                        lng={liveTrackingData.lng}
                        prevLat={prevLivePoint?.lat}
                        prevLng={prevLivePoint?.lng}
                        speed={liveTrackingData.speed}
                        plate={vehicle.plateNo}
                        rotationOffset={0}
                      />
                    )}

                {/* Destination Circle - Delivery Location */}
                                {/* Traffic overlay - only in LIVE mode and when enabled */}
                                {showTraffic && (
                                  // enable autoRefresh on TrafficLayer for near-real-time updates
                                  <>
                                    {/* @ts-ignore - react-google-maps types may not expose TrafficLayer props */}
                                    <TrafficLayer options={{ autoRefresh: true }} />
                                  </>
                                )}
                {destinationLat && destinationLng && (
                  <Circle
                    center={{ lat: destinationLat, lng: destinationLng }}
                    radius={geofenceRadius || 500}
                    options={{
                      fillColor: "#F56565",
                      fillOpacity: 0.2,
                      strokeColor: "#E53E3E",
                      strokeOpacity: 0.8,
                      strokeWeight: 2,
                      visible: false,
                    }}
                    onLoad={(circle) => {
                      destinationCircleRef.current = circle;
                      if (showDestination && map) {
                        circle.setMap(map);
                        circle.setVisible(true);
                      } else {
                        circle.setMap(null);
                      }
                    }}
                    onUnmount={() => {
                      if (destinationCircleRef.current) {
                        destinationCircleRef.current.setMap(null);
                        destinationCircleRef.current = null;
                      }
                    }}
                  />
                )}
              </GoogleMap>

              {/* MovingVehicleMarker overlay used instead of HTML marker */}
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default VehicleMapModal;
